// Projeto da emissão (dw.DimEmissao.dim_projeto_id, vem como `projeto_id` em
// GET/POST /v1/emissoes/). Toda decisão compara SEMPRE o id — `projeto_nome`
// é só pra exibir, nunca pra lógica.
//   1 = emissões da Leverage (tudo o que existia antes)
//   2 = emissões do BTG que monitoramos (sem Akrual: sem PU, situação,
//       carteira nem integralização — ver `possuiDadosAkrual`)
// Quem vê o projeto 2 é decidido no backend (equipe Leverage + e-mails
// autorizados do Dash. BTG) e chega em `user.projetosVisiveis` (/auth/me).
import { addMonths, defaultPeriod } from './period';

export const PROJETO_LEVERAGE = 1;
export const PROJETO_BTG = 2;

// Rótulos do seletor Leverage/BTG em EmissoesEscolha — fixos aqui (por id),
// não lidos de projeto_nome.
export const ROTULO_PROJETO = {
  [PROJETO_LEVERAGE]: 'Leverage',
  [PROJETO_BTG]: 'BTG',
};

// Flags temporárias da visão BTG. Sem o recurso, o BTG fica só em
// /emissoes/escolha (+ aba Características da emissão):
//  - SHOW_BTG_PAINEL: seletor Leverage/BTG no /painel (Painel2) e entrada no
//    Painel sem favorito pra quem vê o BTG (App.jsx);
//  - SHOW_BTG_ENQUADRAMENTO: aba Enquadramento na emissão BTG (Detalhe2/NavBar,
//    via abasPermitidas abaixo).
// BTG_SO_INTERNO true: os dois ficam só pra equipe Leverage (e-mail
// @leveragesec.com.br) — em produção sem liberar pros clientes ainda. Pra
// liberar pra todo mundo que vê o BTG, false. Decidir sempre pelas funções
// btgPainelLiberado/btgEnquadramentoLiberado, nunca pelas flags direto.
export const SHOW_BTG_PAINEL = true;
export const SHOW_BTG_ENQUADRAMENTO = true;
export const BTG_SO_INTERNO = false;

// Mesmo critério de isLeverageUser (NavBar.jsx) — repetido aqui porque NavBar
// importa navAbas → projetos (importar de volta faria ciclo).
function ehEquipeLeverage(user) {
  return !!(user && user.email && user.email.toLowerCase().endsWith('@leveragesec.com.br'));
}
// `simulaCliente`: equipe Leverage vendo a tela como cliente (VisaoToggle
// Pública/BTG) — conta como cliente, senão a simulação mostraria o que é só
// interno.
const liberadoPraEquipe = (user, simulaCliente) => !BTG_SO_INTERNO || (ehEquipeLeverage(user) && !simulaCliente);
export function btgPainelLiberado(user, simulaCliente = false) {
  return SHOW_BTG_PAINEL && liberadoPraEquipe(user, simulaCliente);
}
export function btgEnquadramentoLiberado(user, simulaCliente = false) {
  return SHOW_BTG_ENQUADRAMENTO && liberadoPraEquipe(user, simulaCliente);
}
// Aba Obrigações da emissão BTG — mesma trava BTG_SO_INTERNO. A flag geral
// da aba (SHOW_OBRIGACOES) mora em navAbas.js.
export function btgObrigacoesLiberado(user, simulaCliente = false) {
  return liberadoPraEquipe(user, simulaCliente);
}

export function podeVerProjetoBtg(user) {
  return !!(user && Array.isArray(user.projetosVisiveis) && user.projetosVisiveis.includes(PROJETO_BTG));
}

// Emissão sem `projetoId` (resposta antiga/sem o campo) conta como Leverage.
export function projetoDaEmissao(emissao) {
  return (emissao && emissao.projetoId) || PROJETO_LEVERAGE;
}

// Se a emissão aparece na visão do `projeto` (projetos_exibicao da API: o dono
// + projetos adicionais — hoje só as 45 e 102, do projeto 1, também no BTG).
// Só pra recorte de visão (listas/Painel); abas, estrela, Akrual etc. seguem
// pelo dono (projetoDaEmissao). Sem o campo (API antiga) = só o dono.
export function emissaoNoProjeto(emissao, projeto) {
  const lista = emissao && emissao.projetosExibicao;
  return Array.isArray(lista) && lista.length ? lista.includes(projeto) : projetoDaEmissao(emissao) === projeto;
}

// Emissão "só BTG": dona é o projeto BTG e não aparece na visão Leverage —
// as híbridas (projeto 1 exibidas também no BTG, hoje 45 e 102) não contam.
export function emissaoSoBtg(emissao) {
  return projetoDaEmissao(emissao) === PROJETO_BTG && !emissaoNoProjeto(emissao, PROJETO_LEVERAGE);
}

// Enquadramento das emissões só BTG mostra só m-2 e m-1 (27/10/2026), no
// Painel (visão BTG) e na aba da emissão. Recorta `periodos` do payload de
// /v1/indicadores (tudo — tabela, gráficos, histórico — lê de lá), sem
// mexer no objeto do cache. Leverage/híbridas voltam intactas.
export const MESES_ENQUADRAMENTO_SO_BTG = 2;
export function recortaEnquadramentoSoBtg(emissao, payload) {
  if (!payload || !Array.isArray(payload.periodos) || !emissaoSoBtg(emissao)) return payload;
  const desde = String(addMonths(defaultPeriod(), -(MESES_ENQUADRAMENTO_SO_BTG - 1)));
  const fim = defaultPeriod();
  return { ...payload, periodos: payload.periodos.filter(p => String(p.periodo) >= desde && String(p.periodo) <= fim) };
}

// Filtro "Companhia emissora" da visão BTG (EmissoesEscolha e Painel2).
// SEM_COMPANHIA = chave do filtro pra emissão sem companhia_emissora.
export const SEM_COMPANHIA = '__sem__';
export function companhiaDaEmissao(emissao) {
  return (emissao && emissao.companhiaEmissora) || SEM_COMPANHIA;
}
// Opções do MultiSelectSearchFilter: companhias distintas em ordem alfabética,
// "Sem companhia emissora" no fim.
export function opcoesCompanhiaEmissora(emissoes) {
  const set = new Set((emissoes || []).map(companhiaDaEmissao));
  return [...set].sort((a, b) => (a === SEM_COMPANHIA) - (b === SEM_COMPANHIA) || String(a).localeCompare(String(b), 'pt-BR', { numeric: true }))
    .map(k => ({ key: k, label: k === SEM_COMPANHIA ? 'Sem companhia emissora' : k }));
}

// Nome curto da companhia (card "Securitizadora" do Painel > Enquadramento):
// só a primeira palavra — "Bali Secu" → "Bali". Companhias diferentes com a
// mesma primeira palavra caem no mesmo item do card.
export function nomeCurtoCompanhia(key) {
  if (key === SEM_COMPANHIA) return 'Sem companhia';
  return String(key).trim().split(/\s+/)[0].replace(/[.,;:-]+$/, '') || String(key);
}

// Abas do Detalhe da emissão (/emissoes/:id/<aba>) liberadas por projeto —
// projeto ausente daqui = todas. Obrigações é só do BTG (27/10/2026): some
// das emissões Leverage. Decisão de produto "por enquanto": emissão BTG só
// tem Características, Enquadramento e Obrigações (as duas últimas atrás de
// btgEnquadramentoLiberado/btgObrigacoesLiberado — dependem de quem vê, por
// isso `user`).
const ABAS_POR_PROJETO = {
  [PROJETO_LEVERAGE]: () => ['caracteristicas', 'enquadramento', 'fluxo', 'documentos'],
  [PROJETO_BTG]: (user, simulaCliente) => ['caracteristicas',
    ...(btgEnquadramentoLiberado(user, simulaCliente) ? ['enquadramento'] : []),
    ...(btgObrigacoesLiberado(user, simulaCliente) ? ['obrigacoes'] : [])],
};

// Lista de ids de aba permitidos pra emissão, ou null = sem restrição.
export function abasPermitidas(emissao, user, simulaCliente = false) {
  const abas = ABAS_POR_PROJETO[projetoDaEmissao(emissao)];
  return abas ? abas(user, simulaCliente) : null;
}
